import type { ReactNode } from 'react';
import { SwipeRow } from '../components/SwipeRow';
import { Alert, Button, TextField } from '../components/ui';
import { euro } from '../format';
import { displayCode, NOT_RECEIVED_TAG, warningText } from '../pos/cart';
import type { CartLine } from '../pos/types';
import { LinePrice, Sheet } from './posParts';
import type { Warning } from './posRegisterHelpers';

/** Reine Anzeige-Bausteine der Kassen-Seite (`pos.tsx`): sie halten keinen Zustand und melden Wünsche nur zurück. */

/** Hinweis „Gerät abgekoppelt oder gesperrt“ mit Notausgang. */
export function RevokedAlert({ onRetry, onExport }: { onRetry: () => void; onExport: () => void }) {
  return (
    <Alert kind="error">
      <div className="stack">
        <strong>Dieses Gerät wurde abgekoppelt oder gesperrt.</strong>
        <span>
          Verkaufen ist gesperrt. Ihre Verkäufe bleiben gespeichert: Sichern Sie sie über den
          Notausgang und fragen Sie das Team, ob das Gerät sie noch hochladen darf.
        </span>
        <div className="row">
          <Button small onClick={onRetry}>
            Erneut versuchen
          </Button>
          <Button small variant="secondary" onClick={onExport}>
            Notausgang: Daten als Datei sichern
          </Button>
        </div>
      </div>
    </Alert>
  );
}

/** Eine Zeile des Warenkorbs (wischen zum Entfernen, tippen für Einzelheiten). `no`: 1 = zuerst erfasst. */
export function CartRow({
  line,
  no,
  flashing,
  disabled,
  promoRunning,
  onRemove,
  onOpen,
}: {
  line: CartLine;
  no: number;
  flashing: boolean;
  disabled: boolean;
  promoRunning: boolean;
  onRemove: () => void;
  onOpen: () => void;
}) {
  return (
    <SwipeRow
      className={flashing ? 'cash__line--flash' : ''}
      disabled={disabled}
      onRemove={onRemove}
      onTap={onOpen}
    >
      <span className="cash__no" aria-hidden="true">
        {no}
      </span>
      <button
        type="button"
        className="cash__line-main"
        aria-label={`${line.title}: Einzelheiten anzeigen`}
      >
        <strong>{line.title}</strong>
        <div className="cash__code">
          {line.sellerNumber === null ? 'unbekannt' : `V${String(line.sellerNumber)}`}
          {line.size === null ? '' : ` · Gr. ${line.size}`}
          {` · ${displayCode(line.code)}`}
          {line.override === 'not_received' && ` · ${NOT_RECEIVED_TAG}`}
          {line.override !== null && line.override !== 'not_received' && ' · trotz Warnung'}
          {promoRunning && (line.promo === null || line.promo === undefined)
            ? ' · keine Aktion'
            : ''}
        </div>
      </button>
      <div className="row cash__line-end" data-noswipe="">
        <LinePrice line={line} />
        <button
          type="button"
          className="cash__x"
          aria-label={`${line.title} entfernen`}
          onClick={onRemove}
        >
          <span aria-hidden="true">✕</span>
        </button>
      </div>
    </SwipeRow>
  );
}

/** Anzeige nach dem Kassieren („Bezahlt“, Rückgeld) mit den Knöpfen für den nächsten Kunden und die Kundenansicht. */
export function PaidSummary({
  paid,
  modal,
  onNext,
  onCustomer,
  children,
}: {
  paid: { total: number; change: number | null; card?: boolean };
  modal: boolean;
  onNext: () => void;
  onCustomer: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="cash__done" role="status">
      <p>Bezahlt: {euro(paid.total)}</p>
      {paid.card === true ? (
        <p className="muted">Mit Karte markiert, kein Rückgeld.</p>
      ) : paid.change === null ? (
        <p className="muted">Ohne Betrag kassiert, kein Rückgeld.</p>
      ) : (
        <>
          <p>Rückgeld</p>
          <p className="cash__change">{euro(paid.change)}</p>
        </>
      )}
      <Button className="cash__pay" onClick={onNext}>
        Nächster Kunde
      </Button>
      <Button variant="secondary" disabled={modal} onClick={onCustomer}>
        Kundenansicht
      </Button>
      {children}
    </div>
  );
}

/** Blatt „Warnung“ beim Erfassen: Hinweistext, bei unbekanntem Code Preisfeld, Wege weiter. Die Werte hält die Seite. */
export function WarningSheet({
  warning,
  noLabelOn,
  manualPrice,
  error,
  onPrice,
  onCancel,
  onUpdate,
  onManualItem,
  onConfirm,
}: {
  warning: Warning;
  noLabelOn: boolean;
  manualPrice: string;
  error: string | null;
  onPrice: (value: string) => void;
  onCancel: () => void;
  onUpdate: () => void;
  onManualItem: () => void;
  onConfirm: () => void;
}) {
  return (
    <Sheet
      title="Warnung"
      role="alertdialog"
      onClose={onCancel}
      {...(warning.item === null && !noLabelOn ? { describedBy: 'warning-no-label' } : {})}
    >
      <strong>{warningText(warning.reason)}</strong>
      {warning.item !== null && (
        <span>
          Verkäufer {warning.item.sellerNumber} · {warning.item.title} ·{' '}
          {euro(warning.item.priceCents)}
        </span>
      )}
      {warning.item === null && !noLabelOn && (
        <span className="muted" id="warning-no-label">
          Dieser Code ist der Kasse nicht bekannt. Stand aktualisieren oder Artikel zurücklegen.
          Freie Beträge sind für diesen Basar ausgeschaltet.
        </span>
      )}
      {warning.item === null && !noLabelOn && (
        <Button icon="sync" variant="secondary" onClick={onUpdate}>
          Stand aktualisieren
        </Button>
      )}
      {warning.item === null && noLabelOn && (
        <TextField
          label="Preis in Euro"
          inputMode="decimal"
          value={manualPrice}
          onChange={(change) => {
            onPrice(change.target.value);
          }}
        />
      )}
      {error !== null && <Alert kind="error">{error}</Alert>}
      {warning.item === null && noLabelOn && (
        <Button variant="secondary" onClick={onManualItem}>
          Als Artikel ohne Etikett erfassen
        </Button>
      )}
      <div className="row">
        {(warning.item !== null || noLabelOn) && (
          <Button onClick={onConfirm}>Trotzdem kassieren</Button>
        )}
        <Button variant="secondary" onClick={onCancel}>
          Abbrechen
        </Button>
      </div>
    </Sheet>
  );
}
